// BUILD-039 "Show inactive" for the Settings > User Management > Directory table.
//
// Pure presentation logic (no React, no network, no import.meta.env) so the REAL rules the screen
// runs are testable under plain Node. SettingsPage.tsx imports these functions; it does not
// re-implement them.
//
// WHAT "INACTIVE" MEANS HERE (one definition, one source):
//   employee.globalStatus === 'disabled'
// That is the company-wide Employee status the platform already stores and the HR roster already
// filters on. It is NOT any of these, and none of them may hide a row:
//   * "No Unique HQ login"  - a person can legitimately have no HQ login (e.g. an HR-only record)
//   * a revoked per-app grant (HQ / Pricing / PM access off)
//   * a Cognito login that is disabled (shown as its own "Login disabled" fact, never filtered)
//   * employment status - this is never an employment-termination signal
//
// PENDING INVITATIONS ARE NEVER HIDDEN. A login still waiting for its first sign-in
// (FORCE_CHANGE_PASSWORD) holds an outstanding invitation credential, so it stays visible whatever
// the toggle says, even if the same person is also marked inactive (both badges are shown).
//
// THE LOGIN LIST IS PAGINATED. usersDirectoryLoader.ts follows the provider's continuation token, one
// bounded page at a time. This module only decides what the screen may SAY about the result:
// a COMPLETE list may call a person "No Unique HQ login"; an INCOMPLETE list (a later page failed,
// the page bound was hit, or an older server cannot say) may not, because that person's login might be on a
// page that was never read. Incomplete lists carry a visible notice and an "incomplete" count caveat.
import type { IncompleteReason } from './usersDirectoryLoader';

export type LoginListStatus =
  | { complete: true }
  | { complete: false; reason: IncompleteReason; loaded: number; error?: string };

export interface DirectoryRowLike {
  /** Cognito UserStatus of the HQ login, or null when the person has no HQ login in the loaded list. */
  cognitoStatus: string | null;
  /** Cognito Enabled flag of the HQ login when known; null/undefined when unknown or no login. */
  loginEnabled?: boolean | null;
  /** The canonical Employee, when one is linked. */
  employee: { globalStatus: 'active' | 'disabled' | null } | null;
}

export interface DirectoryRowState {
  /** employee.globalStatus === 'disabled' (the ONLY definition of inactive). */
  inactive: boolean;
  /** HQ login that has not completed first sign-in (outstanding invitation). */
  pending: boolean;
  /** No HQ login in the loaded list. Says nothing about being inactive. */
  noLogin: boolean;
  /** noLogin, but the login list is incomplete so a login may exist on a page that was not read. */
  loginUnconfirmed: boolean;
  /** The HQ login exists but Cognito reports it disabled. Display fact only; never filters. */
  loginDisabled: boolean;
}

export function rowState(row: DirectoryRowLike, loginListComplete = true): DirectoryRowState {
  const noLogin = row.cognitoStatus === null;
  return {
    inactive: row.employee?.globalStatus === 'disabled',
    pending: row.cognitoStatus === 'FORCE_CHANGE_PASSWORD',
    noLogin,
    loginUnconfirmed: noLogin && !loginListComplete,
    loginDisabled: row.cognitoStatus !== null && row.loginEnabled === false,
  };
}

/** A row is visible when the toggle is on, the person is not inactive, or an invitation is pending. */
export function isRowVisible(row: DirectoryRowLike, showInactive: boolean): boolean {
  const s = rowState(row);
  return showInactive || !s.inactive || s.pending;
}

/** Order-preserving filter. Never mutates its input and never drops a row that is not inactive. */
export function filterDirectoryRows<T extends DirectoryRowLike>(rows: T[], showInactive: boolean): T[] {
  return rows.filter((r) => isRowVisible(r, showInactive));
}

export interface DirectoryContext {
  /** Whether every page of HQ logins was read, and if not, why. */
  loginList: LoginListStatus;
  /** True when the Employee list loaded successfully. If false, inactive status is UNKNOWN. */
  employeesLoaded: boolean;
}

export interface DirectorySummary {
  total: number;
  shown: number;
  /** Inactive people hidden by the current toggle position (0 when the toggle is on). */
  hiddenInactive: number;
  /** All inactive people in the loaded rows, whatever the toggle says. */
  inactiveTotal: number;
  pendingTotal: number;
  noLoginTotal: number;
  /** Rows without a loaded login whose login could not be confirmed absent (incomplete list). */
  loginUnconfirmedTotal: number;
  /** True when not every page of logins was read. The directory must not be presented as complete. */
  listIncomplete: boolean;
  loginList: LoginListStatus;
  /** False when the Employee list is unavailable: nobody can be classified inactive. */
  inactiveStatusKnown: boolean;
}

export function summarizeDirectory<T extends DirectoryRowLike>(rows: T[], showInactive: boolean, ctx: DirectoryContext): DirectorySummary {
  let inactiveTotal = 0; let pendingTotal = 0; let noLoginTotal = 0; let loginUnconfirmedTotal = 0; let shown = 0;
  for (const r of rows) {
    const s = rowState(r, ctx.loginList.complete);
    if (s.inactive) inactiveTotal += 1;
    if (s.pending) pendingTotal += 1;
    if (s.noLogin) noLoginTotal += 1;
    if (s.loginUnconfirmed) loginUnconfirmedTotal += 1;
    if (isRowVisible(r, showInactive)) shown += 1;
  }
  return {
    total: rows.length,
    shown,
    hiddenInactive: rows.length - shown,
    inactiveTotal,
    pendingTotal,
    noLoginTotal,
    loginUnconfirmedTotal,
    listIncomplete: !ctx.loginList.complete,
    loginList: ctx.loginList,
    inactiveStatusKnown: ctx.employeesLoaded,
  };
}

function peopleWord(n: number): string { return n === 1 ? 'person' : 'people'; }

/** One honest line of counts. Mentions hidden inactive people and the possible-incompleteness caveat. */
export function describeCounts(s: DirectorySummary): string {
  const parts: string[] = [`Showing ${s.shown} of ${s.total} ${peopleWord(s.total)}`];
  if (s.hiddenInactive > 0) parts.push(`${s.hiddenInactive} inactive hidden`);
  if (s.listIncomplete) parts.push('list incomplete');
  return parts.join(' \u00b7 ');
}

/** What the screen shows while pages are still being read. */
export function describeLoadProgress(loaded: number, pages: number): string {
  if (pages <= 0) return 'Loading\u2026';
  return `Loading logins\u2026 ${loaded} loaded so far (page ${pages}). The directory appears when every page has been read.`;
}

/** Notice for an incomplete login list; null when the list is complete. Never claims completeness. */
export function incompleteNotice(s: DirectorySummary): string | null {
  const l = s.loginList;
  if (l.complete) return null;
  const tail = ' People who have a Unique HQ login that was not loaded may be missing, and people without a loaded login are shown as '
    + '\u201cLogin not confirmed\u201d rather than \u201cNo Unique HQ login\u201d. Do not treat this directory (or the inactive filter) as a complete list.';
  switch (l.reason) {
    case 'continuation-failed':
      return `Loaded ${l.loaded} login${l.loaded === 1 ? '' : 's'}, then the next page could not be read${l.error ? ` (${l.error})` : ''}.${tail} Use Retry to load again.`;
    case 'page-limit':
      return `Loaded ${l.loaded} logins, which is the most this screen reads in one go, and more still exist.${tail}`;
    case 'repeated-token':
      return `Loaded ${l.loaded} logins, then the server returned the same continuation twice, so loading stopped to avoid looping.${tail}`;
    case 'server-not-paginated':
      return `The server returned ${l.loaded} logins (a full first page) and cannot say whether there are more; it does not support continuation yet.${tail}`;
    default:
      return `The login list is incomplete.${tail}`;
  }
}

export const NO_LOGIN_LABEL = {
  confirmed: { label: 'No Unique HQ login', title: 'No Unique HQ login exists for this person in the complete login list.' },
  unconfirmed: { label: 'Login not confirmed', title: 'The login list is incomplete, so this person may still have a Unique HQ login that was not loaded.' },
} as const;

/** Text for the empty state when rows exist but every one is hidden by the filter. */
export function emptyFilteredMessage(s: DirectorySummary): string | null {
  if (s.total === 0 || s.shown > 0) return null;
  return `Everyone loaded is inactive (${s.hiddenInactive}). Turn on \u201cShow inactive\u201d to see them.`;
}

/** Label and hover text for the toggle. */
export function toggleLabel(s: DirectorySummary): string {
  return s.inactiveStatusKnown ? `Show inactive (${s.inactiveTotal})` : 'Show inactive';
}

export function toggleTitle(s: DirectorySummary): string {
  if (!s.inactiveStatusKnown) {
    return 'Inactive status is unavailable because the employee list did not load, so no one can be hidden or labelled inactive right now.';
  }
  return 'Inactive means the person\u2019s company-wide Employee status is disabled. Their employee record, history and audit trail are kept. '
    + 'It is not an employment-termination signal, and having no Unique HQ login does not make someone inactive.';
}

export const INACTIVE_BADGE = {
  label: 'Inactive',
  className: 'bg-gray-200 text-gray-700',
  title: 'Company-wide Employee status is disabled. The employee record and history are kept. This is not an employment-termination signal.',
} as const;

export const LOGIN_DISABLED_BADGE = {
  label: 'Login disabled',
  className: 'bg-amber-100 text-amber-800',
  title: 'The Unique HQ sign-in is disabled in the identity provider. This is separate from the person\u2019s company-wide Employee status.',
} as const;
